import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import ar from './locales/ar.json';

// Auto-merge any per-namespace locale files placed under ./locales/generated
// (e.g. en.dashboard.json, ar.reviews.json) so translation work can be split
// across many files without merge conflicts in a single monolithic resource.
// A recursive merge lets multiple files contribute to the same top-level
// namespace (e.g. several files each adding keys under "admin").
const generated = import.meta.glob('./locales/generated/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, unknown> }
>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMerge(
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> {
  for (const [key, value] of Object.entries(source)) {
    if (isPlainObject(value) && isPlainObject(target[key])) {
      deepMerge(target[key] as Record<string, unknown>, value);
    } else {
      target[key] = value;
    }
  }
  return target;
}

function collectGenerated(lang: 'en' | 'ar'): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const [path, mod] of Object.entries(generated)) {
    const file = path.split('/').pop() || '';
    if (file.startsWith(`${lang}.`)) {
      deepMerge(merged, mod.default);
    }
  }
  return merged;
}

const enResources = deepMerge({ ...en }, collectGenerated('en'));
const arResources = deepMerge({ ...ar }, collectGenerated('ar'));

export const SUPPORTED_LANGUAGES = ['en', 'ar'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const RTL_LANGUAGES: SupportedLanguage[] = ['ar'];

export function isRtlLanguage(lng: string | undefined): boolean {
  return RTL_LANGUAGES.includes((lng || '').split('-')[0] as SupportedLanguage);
}

/**
 * Keep the <html> lang/dir attributes in sync with the active language so that
 * RTL languages (Arabic) render correctly across the whole app.
 */
export function applyDocumentDirection(lng: string | undefined): void {
  if (typeof document === 'undefined') return;
  const base = (lng || 'en').split('-')[0];
  const dir = isRtlLanguage(base) ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('lang', base);
  document.documentElement.setAttribute('dir', dir);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enResources },
      ar: { translation: arResources },
    },
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'neurev_language',
    },
  });

applyDocumentDirection(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', (lng) => applyDocumentDirection(lng));

export default i18n;
